import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Interiors & Lifestyle Spaces",
  "Personal Styling & Image Curation",
  "Luxury Gifting",
  "Events & Experiences",
  "Brand Identity",
  "Creative Direction",
  "Lifestyle Strategy",
  "Content & Communication",
  "Bespoke VIP Service",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a brief message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success(
        data?.email_sent
          ? "Thank you. Rishabh has been notified personally."
          : "Thank you — your note has been received. We'll be in touch."
      );
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-cream/5 pt-24 md:pt-32 pb-24"
      style={{
        background:
          "linear-gradient(180deg, #050604 0%, #020301 30%, #000000 70%, #000000 100%)",
      }}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 text-brass mb-10">
          <span className="w-8 h-px bg-brass" />
          <span className="text-[10px] md:text-[11px] tracking-overline uppercase font-medium">
            Begin a conversation
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          {/* Left */}
          <div className="md:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-light text-5xl md:text-6xl lg:text-7xl tracking-editorial leading-[0.95] text-cream"
            >
              We don't<br />
              <span className="italic text-brass">bite.</span>
            </motion.h2>

            {/* Portrait — Rishabh & the studio dog. Feathered into pure black on all edges. */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 relative w-full max-w-lg"
              data-testid="contact-portrait-frame"
            >
              <img
                src="/assets/contact-portrait.png"
                alt="Rishabh and the studio dog"
                className="w-full h-auto block"
                style={{
                  filter:
                    "grayscale(100%) brightness(1.28) contrast(1.15) saturate(1)",
                  mixBlendMode: "screen",
                  imageRendering: "auto",
                  WebkitMaskImage:
                    "radial-gradient(ellipse 82% 88% at 50% 45%, #000 40%, rgba(0,0,0,0.75) 70%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "radial-gradient(ellipse 82% 88% at 50% 45%, #000 40%, rgba(0,0,0,0.75) 70%, rgba(0,0,0,0) 100%)",
                }}
                draggable={false}
              />
              {/* Bottom fade into black */}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 75%, #000 100%)",
                }}
              />
              {/* Caption */}
              <div className="mt-4 flex items-center gap-2 text-[10px] tracking-overline uppercase text-cream/60">
                <span className="w-4 h-px bg-brass" />
                <span>Rishabh &amp; The Studio Companion</span>
              </div>
            </motion.div>

            <p className="mt-8 max-w-md text-base md:text-lg text-cream/70 leading-relaxed font-light">
              Tell us what you're imagining — or what you can't yet describe.
              A first conversation is complimentary, confidential and considered.
            </p>

            <div className="mt-14 space-y-8">
              <div>
                <div className="text-[10px] tracking-overline uppercase text-brass mb-2">Telephone</div>
                <a
                  href="tel:+919958721362"
                  data-testid="contact-phone"
                  className="font-serif text-2xl md:text-3xl text-cream link-underline"
                >
                  +91 99587 21362
                </a>
              </div>
              <div>
                <div className="text-[10px] tracking-overline uppercase text-brass mb-2">Correspondence</div>
                <a
                  href="mailto:daftar@ridesign.in"
                  data-testid="contact-email"
                  className="font-serif text-2xl md:text-3xl text-cream link-underline"
                >
                  daftar@ridesign.in
                </a>
              </div>
              <div>
                <div className="text-[10px] tracking-overline uppercase text-brass mb-2">Hours</div>
                <p className="font-serif text-lg md:text-xl text-cream/80 leading-snug">
                  Monday – Saturday · 9:00 am – 9:00 pm<br />
                  Sunday · 12:00 pm – 10:00 pm
                </p>
              </div>

              {/* Private WhatsApp line — discreet, direct channel for HNI clients */}
              <div>
                <div className="text-[10px] tracking-overline uppercase text-brass mb-3">Private Line</div>
                <a
                  href="https://wa.me/919958721362?text=Hello%20%E0%A4%8B%2C%20I%27d%20like%20to%20begin%20a%20conversation"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="group inline-flex items-center gap-4 border border-brass/40 pl-5 pr-6 py-4 hover:border-brass hover:bg-brass/10 transition-colors duration-500"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full border border-brass/50 text-brass group-hover:bg-brass group-hover:text-onyx transition-colors duration-500">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]" aria-hidden>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </span>
                  <span className="flex flex-col">
                    <span className="font-serif text-xl md:text-2xl text-cream leading-tight">Message on WhatsApp</span>
                    <span className="text-[10px] tracking-overline uppercase text-cream/50 mt-1">Discreet · Direct to Rishabh</span>
                  </span>
                  <span className="ml-1 text-brass transition-transform duration-500 group-hover:translate-x-1">→</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="md:col-span-7">
            {!sent ? (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
              >
                <div className="md:col-span-1">
                  <label className="text-[10px] tracking-overline uppercase text-cream/50">Name</label>
                  <input
                    data-testid="contact-input-name"
                    className="editorial-input w-full"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="text-[10px] tracking-overline uppercase text-cream/50">Email</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    className="editorial-input w-full"
                    placeholder="you@domain.com"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="text-[10px] tracking-overline uppercase text-cream/50">Phone (optional)</label>
                  <input
                    data-testid="contact-input-phone"
                    className="editorial-input w-full"
                    placeholder="+91 —"
                    value={form.phone}
                    onChange={update("phone")}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="text-[10px] tracking-overline uppercase text-cream/50">Service of interest</label>
                  <Select value={form.service} onValueChange={(v) => update("service")(v)}>
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className="editorial-input w-full h-auto text-left justify-start !py-[14px] !px-0 rounded-none data-[placeholder]:text-cream-faint text-cream"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-onyx-soft border-cream/15 text-cream">
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          data-testid={`contact-service-option-${s.toLowerCase().replace(/\s|&/g, "-")}`}
                          className="text-cream/90 focus:bg-brass/20 focus:text-cream data-[state=checked]:text-brass"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 mt-4">
                  <label className="text-[10px] tracking-overline uppercase text-cream/50">Message</label>
                  <textarea
                    data-testid="contact-input-message"
                    rows={4}
                    className="editorial-input w-full resize-none"
                    placeholder="Tell us a little about what you're imagining…"
                    value={form.message}
                    onChange={update("message")}
                    required
                  />
                </div>

                <div className="md:col-span-2 mt-10 flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
                  <p className="text-[11px] text-cream/50 font-light max-w-md leading-relaxed">
                    By submitting, you agree to be contacted in strict confidence.
                    We reply personally, usually within one working day.
                  </p>
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center gap-3 self-start text-[11px] font-sans tracking-overline uppercase text-onyx bg-cream hover:bg-brass transition-colors duration-500 px-8 py-4 disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Send Inquiry"}
                    <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
                  </button>
                </div>
              </form>
            ) : (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="min-h-[420px] flex flex-col justify-center border border-cream/15 p-10 md:p-14 bg-onyx"
              >
                <div className="logo-mark w-14 h-14 md:w-16 md:h-16 mb-6">
                  <img src="/assets/logo-mark.png" alt="ऋ Design monogram" />
                </div>
                <h3 className="font-serif italic text-4xl md:text-5xl text-cream tracking-editorial leading-tight">
                  Thank you.
                </h3>
                <p className="mt-6 max-w-lg text-cream/70 leading-relaxed font-light">
                  Your note is with us. Rishabh will personally reply,
                  usually within a working day. Until then — enjoy the quiet.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                  className="mt-10 self-start text-[11px] tracking-overline uppercase text-brass link-underline"
                >
                  Send another
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
